import React, { useState } from 'react';
import { 
  X, 
  Lock, 
  User, 
  Loader2, 
  ShieldCheck, 
  CheckCircle2, 
  KeyRound, 
  CreditCard,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../api/client';
import { showToast } from '../common/Toast';
import PasswordInput from '../common/PasswordInput';
import SubscriptionModal from '../subscription/SubscriptionModal';

export default function AuthModal({ isOpen, onClose, onSuccess = null, initialMode = 'login' }) {
  const { login, logout } = useAuth();
  const { t, currentLang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form Fields for Login
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [step2Password, setStep2Password] = useState('');

  // Subscription Modal Trigger
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('CLIENT');

  if (!isOpen && !subscriptionModalOpen) return null;

  // Helper to check monthly subscription requirement and route user
  const checkSubscriptionAndProceed = async (authenticatedUser) => {
    try {
      const statusRes = await api.get('/subscriptions/my-status');
      if (statusRes?.success) {
        if (statusRes.isFreeExempt || statusRes.isActive) {
          showToast(
            currentLang === 'en'
              ? `Welcome, ${authenticatedUser.fullName || 'User'}!`
              : currentLang === 'ru'
              ? `Добро пожаловать, ${authenticatedUser.fullName || 'Пользователь'}!`
              : `Xush kelibsiz, ${authenticatedUser.fullName || 'Foydalanuvchi'}!`,
            'success'
          );
          if (onSuccess) onSuccess();
          onClose();
          if (authenticatedUser.role === 'ADMIN' || authenticatedUser.role === 'SUPER_ADMIN' || authenticatedUser.role === 'LAWYER') {
            window.location.href = '/admin/dashboard';
          }
          return;
        }
      }
    } catch (err) {
      console.warn('Subscription check error:', err);
    }

    // Unpaid / expired subscription detected -> require monthly subscription activation
    const isLawyerRole = authenticatedUser.role === 'LAWYER';
    setSubscriptionType(isLawyerRole ? 'LAWYER' : 'CLIENT');
    setPendingUser(authenticatedUser);
    setSubscriptionModalOpen(true);
    showToast(
      isLawyerRole
        ? currentLang === 'en'
          ? 'Activate 99,000 UZS/mo subscription to enter attorney portal'
          : currentLang === 'ru'
          ? 'Активируйте подписку за 99 000 сум/мес для входа в кабинет адвоката'
          : 'Advokat kabinetiga kirish uchun 99 000 so‘mlik oylik obunani faollashtiring'
        : currentLang === 'en'
        ? 'Activate 35,000 UZS/mo subscription to access services'
        : currentLang === 'ru'
        ? 'Активируйте подписку за 35 000 сум/мес для полного доступа'
        : 'Xizmatlardan to‘liq foydalanish uchun 35 000 so‘mlik oylik obunani faollashtiring',
      'info'
    );
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!loginInput.trim()) {
      setError(
        currentLang === 'en'
          ? 'Please enter your login, phone number, or email'
          : currentLang === 'ru'
          ? 'Введите логин, номер телефона или email'
          : 'Login, telefon raqam yoki emailni kiriting'
      );
      return;
    }

    if (password.length < 6) {
      setError(
        currentLang === 'en'
          ? 'Password must be at least 6 characters'
          : currentLang === 'ru'
          ? 'Пароль должен содержать не менее 6 символов'
          : 'Parol kamida 6 ta belgidan iborat bo‘lishi kerak'
      );
      return;
    }

    setLoading(true);
    try {
      const result = await login(
        loginInput.trim(),
        password,
        twoFactorRequired ? step2Password : undefined
      );

      if (result?.requiresTwoFactor) {
        setTwoFactorRequired(true);
        setError('');
        showToast(
          currentLang === 'en'
            ? 'Step 1 verified! Please enter your 2-step security password.'
            : currentLang === 'ru'
            ? 'Шаг 1 подтверждён! Введите секретный пароль 2-го этапа.'
            : '1-bosqich tasdiqlandi! 2-bosqich maxfiy parolini kiriting.',
          'info'
        );
        return;
      }

      // Super Admin & Admin direct bypass
      if (result.role === 'ADMIN' || result.role === 'SUPER_ADMIN') {
        showToast(
          currentLang === 'en'
            ? `Welcome, ${result.fullName || 'Administrator'}!`
            : currentLang === 'ru'
            ? `Добро пожаловать, ${result.fullName || 'Администратор'}!`
            : `Xush kelibsiz, ${result.fullName || 'Administrator'}!`,
          'success'
        );
        if (onSuccess) onSuccess();
        onClose();
        window.location.href = '/admin/dashboard';
        return;
      }

      // Check Lawyer or Client monthly subscription
    } catch (err) {
      if (err.data?.error === 'SubscriptionPaymentRequired' || err.status === 403) {
        const targetType = err.data?.targetType || 'CLIENT';
        setSubscriptionType(targetType);
        setSubscriptionModalOpen(true);
        showToast(
          err.message ||
          (currentLang === 'en'
            ? 'Access requires active monthly subscription. Please activate to proceed.'
            : currentLang === 'ru'
            ? 'Для входа требуется активная ежемесячная подписка. Пожалуйста, активируйте её.'
            : 'Akkauntga kirish uchun oylik obuna to‘lanishi kerak. Iltimos, to‘lovni amalga oshiring.'),
          'error'
        );
        return;
      }
      setError(
        err.message ||
        (currentLang === 'en'
          ? 'Incorrect login or password'
          : currentLang === 'ru'
          ? 'Неверный логин или пароль'
          : 'Login yoki parol noto‘g‘ri')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSubscription = () => {
    setSubscriptionType('CLIENT');
    setPendingUser(null);
    setSubscriptionModalOpen(true);
  };

  return (
    <>
      {isOpen && !subscriptionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          {/* Dark Backdrop */}
          <div 
            className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
            onClick={onClose}
          />

          {/* Modal Dialog */}
          <div className="relative w-full max-w-md bg-[#070c16] border border-[#c59b27]/40 rounded-2xl shadow-2xl shadow-black/90 overflow-hidden z-10 my-4 max-h-[92vh] flex flex-col animate-in zoom-in-95 duration-200">
            
            {/* Top Gold Accent Line */}
            <div className="h-1 w-full bg-gradient-to-r from-[#b38038] via-[#c59b27] to-[#d4af37] shrink-0" />

            {/* Clean Header Bar with Non-overlapping X Button */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-[#1b2b46]/60 bg-[#060a14] shrink-0">
              <div className="flex items-center space-x-2.5 min-w-0 pr-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1b273d] to-[#0c1424] border border-[#c59b27]/40 flex items-center justify-center text-[#c59b27] shrink-0 shadow-sm">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif text-base sm:text-lg font-bold text-slate-100 tracking-tight truncate">
                    {t('auth_login_title', 'Tizimga Kirish')}
                  </h3>
                  <p className="text-[10px] text-slate-400 truncate">
                    {t('auth_login_subtitle', 'Advokat, mijoz va ma’muriy xodimlar uchun')}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="w-7 h-7 rounded-full bg-[#111c2e] hover:bg-[#1a2942] border border-slate-700/60 flex items-center justify-center text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0 ml-2"
                title="Yopish (X)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-4 sm:p-5 overflow-y-auto">
              
              {/* Error Message */}
              {error && (
                <div className="mb-3.5 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex flex-col space-y-2 animate-in fade-in duration-150">
                  <div className="flex items-start space-x-2">
                    <span className="font-bold shrink-0">•</span>
                    <span className="leading-relaxed">{error}</span>
                  </div>
                  {(error.includes('obuna') || error.includes('to‘lov') || error.includes('ruxsat')) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSubscriptionType('LAWYER');
                        setSubscriptionModalOpen(true);
                      }}
                      className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:to-[#d4af37] text-slate-950 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-md cursor-pointer mt-1"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>{currentLang === 'en' ? 'Pay Subscription (99,000 UZS)' : currentLang === 'ru' ? 'Оплатить подписку (99 000 сум)' : 'Obunani To‘lash (99 000 UZS)'}</span>
                    </button>
                  )}
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleLoginSubmit} className="space-y-3.5">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                    {t('auth_login_input_label', 'Login, Telefon Raqam yoki Email')} <span className="text-[#c59b27]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-[#c59b27]">
                      <User className="w-3.5 h-3.5" />
                    </div>
                    <input
                      type="text"
                      required
                      placeholder={t('auth_login_input_placeholder', 'Login, telefon raqam yoki email')}
                      value={loginInput}
                      onChange={(e) => setLoginInput(e.target.value)}
                      autoComplete="username"
                      className="w-full pl-9 pr-3 py-2 bg-[#0b1220] border border-[#1b2b46] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#c59b27] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                    {t('auth_password_label', 'Parol')} <span className="text-[#c59b27]">*</span>
                  </label>
                  <PasswordInput
                    required
                    placeholder={t('auth_password_placeholder', 'Kamida 6 ta belgi')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    startIcon={<Lock className="w-3.5 h-3.5" />}
                    className="w-full py-2 bg-[#0b1220] border border-[#1b2b46] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#c59b27] transition-colors"
                  />
                </div>

                {twoFactorRequired && (
                  <div className="space-y-2 pt-1 border-t border-[#1b2b46]/70 animate-in fade-in duration-200">
                    <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] flex items-center space-x-1.5">
                      <KeyRound className="w-3.5 h-3.5 shrink-0 text-amber-400" />
                      <span>{t('auth_2fa_title', '2-bosqich maxfiy xavfsizlik parolini kiriting')}</span>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                        {t('auth_2fa_label', '2-Bosqich Paroli')} <span className="text-[#c59b27]">*</span>
                      </label>
                      <PasswordInput
                        required
                        placeholder="••••••••"
                        value={step2Password}
                        onChange={(e) => setStep2Password(e.target.value)}
                        startIcon={<KeyRound className="w-3.5 h-3.5 text-emerald-400" />}
                        className="w-full py-2 bg-[#0b1220] border border-[#1b2b46] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#c59b27] transition-colors"
                        autoFocus
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-2 py-2.5 px-4 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-slate-950 font-bold text-xs uppercase tracking-wider shadow-lg shadow-[#b38038]/20 transition-all flex items-center justify-center cursor-pointer disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {currentLang === 'en' ? 'Checking...' : currentLang === 'ru' ? 'Проверка...' : 'Tekshirilmoqda...'}
                    </>
                  ) : twoFactorRequired ? (
                    t('auth_btn_2fa_submit', '2-BOSQICHNI TASDIQLASH VA KIRISH')
                  ) : (
                    t('auth_btn_submit', 'TIZIMGA KIRISH')
                  )}
                </button>
              </form>

              {/* NEW CLIENT REGISTRATION THROUGH SUBSCRIPTION PAYMENT ONLY */}
              <div className="mt-4 pt-3.5 border-t border-[#1b2b46]/70">
                <div className="p-3.5 rounded-xl bg-gradient-to-b from-[#0b1322] to-[#080d17] border border-[#c59b27]/30 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#c59b27]" />
                    <span className="text-xs font-bold text-slate-100 font-serif">
                      {t('auth_new_user_title', 'Yangi foydalanuvchimisiz?')}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                    {t('auth_new_user_desc', 'Yuridik yordam olish, advokatlar bilan bevosita bog‘lanish va shaxsiy akkaunt ochish 30 kunlik oylik obuna to‘lovi orqali amalga oshiriladi.')}
                  </p>
                  <button
                    type="button"
                    onClick={handleOpenSubscription}
                    className="w-full py-2.5 px-3 rounded-lg border border-[#c59b27]/60 bg-[#121c2d] hover:bg-[#19273f] hover:border-[#c59b27] text-[#c59b27] hover:text-white text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md text-center leading-normal break-words"
                  >
                    <CreditCard className="w-3.5 h-3.5 text-[#c59b27] shrink-0" />
                    <span>{t('auth_new_user_cta', 'Obuna to‘lash va Akkaunt ochish (35 000 so‘m)')}</span>
                    <ArrowRight className="w-3.5 h-3.5 shrink-0" />
                  </button>
                </div>
              </div>

              {/* Secure Guarantee Footer */}
              <div className="mt-3.5 pt-2.5 border-t border-[#1b2b46]/50 flex items-center justify-center text-[10px] text-slate-400 space-x-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>{t('auth_security_guarantee', 'Ma’lumotlar shifrlangan va advokatlik siriga binoan himoyalangan')}</span>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Monthly Subscription Modal Prompt */}
      {subscriptionModalOpen && (
        <SubscriptionModal
          isOpen={subscriptionModalOpen}
          onClose={() => {
            setSubscriptionModalOpen(false);
            if (pendingUser?.role === 'LAWYER') {
              logout();
              showToast(
                currentLang === 'en'
                  ? 'Attorney portal requires monthly payment confirmation.'
                  : currentLang === 'ru'
                  ? 'Вход в кабинет адвоката требует подтверждения оплаты.'
                  : 'Advokat kabinetiga kirish uchun 99 000 so‘mlik oylik to‘lov tasdiqlanishi shart.',
                'warning'
              );
            }
            onClose();
          }}
          onSuccess={(sub) => {
            setSubscriptionModalOpen(false);
            if (sub?.status === 'ACTIVE' || sub?.isFreeExempt) {
              showToast(
                currentLang === 'en'
                  ? '30-day access activated successfully!'
                  : currentLang === 'ru'
                  ? 'Доступ на 30 дней успешно активирован!'
                  : '30 kunlik muddat muvaffaqiyatli faollashtirildi!',
                'success'
              );
              if (onSuccess) onSuccess();
              onClose();
              if (pendingUser?.role === 'LAWYER' || pendingUser?.role === 'ADMIN' || pendingUser?.role === 'SUPER_ADMIN') {
                window.location.href = '/admin/dashboard';
              }
            } else {
              showToast(
                currentLang === 'en'
                  ? 'Payment receipt submitted! Please wait for administrator verification.'
                  : currentLang === 'ru'
                  ? 'Чек об оплате отправлен на проверку. Доступ откроется после одобрения администратором.'
                  : 'To‘lov cheki tekshiruvga yuborildi! Administrator tasdiqlaganidan so‘ng akkaunt faollashadi.',
                'info'
              );
              onClose();
            }
          }}
        />
      )}
    </>
  );
}
